import React, { useRef, useEffect, useState } from 'react';
import cn from 'classnames';
import moment from 'moment';
import './UrlSiteRuleResults.scss';

const UrlSiteRuleResults = props => {
  const { results, curPage, setCurPage, loadNextPage, selectedSiteRule, setSelectedSiteRule } = props;
  const scrollRef = useRef(null);
  const [didScrollBottom, setDidScrollBottom] = useState(false);

  const handleScroll = () => {
    /*
      Handle infinite scroll loading trigger
    */
    const div = scrollRef.current;
    if (!div) return;

    const { scrollTop, scrollHeight, clientHeight } = div;

    const threshold = 100;

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      setDidScrollBottom(true);
    } else {
      setDidScrollBottom(false);
    }
  };

  useEffect(() => {
    if (!didScrollBottom || !results?.length) return;

    setCurPage(curPage + 1);
    loadNextPage({ pageNum: curPage + 1 });
  }, [didScrollBottom]);

  useEffect(() => {
    const resultsContainer = scrollRef.current;
    if (!resultsContainer) return;

    resultsContainer.addEventListener('scroll', handleScroll);

    return () => {
      resultsContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleSelectedSiteRule = rule => {
    if (selectedSiteRule?.id === rule.id) {
      setSelectedSiteRule(null);
    } else {
      setSelectedSiteRule(rule);
    }
  };

  return (
    <div className='results-container' ref={scrollRef}>
      {results.map(rule => {
        // Rule path constraints
        let numPathConstraints = 0;
        if (rule.paths?.length > 0) numPathConstraints += rule.paths.length;
        if (rule.pathPrefixes?.length > 0) numPathConstraints += rule.pathPrefixes.length;
        if (rule.pathRegexPattern) numPathConstraints += 1;

        // Rule path replacements
        let numPathReplacements = 0;
        if (rule.pathReplacements?.length > 0) numPathReplacements += rule.pathReplacements.length;

        // Custom preserve params
        let numPreserveParams = 0;
        if (rule.preserveParams?.length > 0) numPreserveParams += rule.preserveParams.length;

        // Rule status
        const ruleIsActive = !!rule.isActive;
        const ruleIsScheduledForUpdate = !!rule.needsUpdate;
        const ruleIsScheduledForDeletion = !!rule.needsDeletion;

        let ruleStatus = ruleIsActive ? 'Active' : 'Inactive';
        if (ruleIsScheduledForUpdate) ruleStatus += ' (sync scheduled)';
        if (ruleIsScheduledForDeletion) ruleStatus += ' (deletion scheduled)';

        return (
          <div
            key={rule.id}
            className={cn('rule-card', { 'is-selected': selectedSiteRule?.id === rule.id })}
            onClick={() => toggleSelectedSiteRule(rule)}
          >
            <div className='label'>
              {rule.domain}
              {!!rule.forceWWW && <div className='force-www'> (www)</div>}
            </div>
            <div className='meta-container'>
              <div className='meta-left'>
                <div>{`Created ${moment(rule.createdAt).format('MMM Do')}`}</div>
              </div>
              <div className='meta-right'>
                {!!numPathConstraints && (
                  <>
                    <div>
                      <span>{numPathConstraints}</span>
                      path constraint{numPathConstraints !== 1 ? 's' : ''}
                    </div>
                    {' • '}
                  </>
                )}
                {!!numPathReplacements && (
                  <>
                    <div>
                      <span>{numPathReplacements}</span>
                      path replacement{numPathReplacements !== 1 ? 's' : ''}
                    </div>
                    {' • '}
                  </>
                )}
                {!!numPreserveParams && (
                  <>
                    <div>
                      <span>{numPreserveParams}</span>
                      custom param{numPreserveParams !== 1 ? 's' : ''}
                    </div>
                    {' • '}
                  </>
                )}
                <div
                  className={cn('active-status', {
                    'is-active': ruleIsActive,
                    isUpdate: ruleIsScheduledForUpdate,
                    isDeletion: ruleIsScheduledForDeletion
                  })}
                >
                  {ruleStatus}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UrlSiteRuleResults;
